import { useCallback, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { productsService, type ProductImportPreview } from '../../../services/products/products.service';

type UseProductImportExportParams = {
  tenantId: string;
  employeeSession: string | null;
  reloadVariantComboCounts: () => void;
  onError: (message: string) => void;
};

// Architectural summary: products .xlsx export + import-modal open/close + post-commit refresh.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks
export function useProductImportExport({
  tenantId,
  employeeSession,
  reloadVariantComboCounts,
  onError,
}: UseProductImportExportParams) {
  const [isImportOpen, setIsImportOpen] = useState(false);

  const exportProducts = useCallback(() => {
    if (!employeeSession) {
      return;
    }
    void productsService
      .exportXlsx(tenantId, employeeSession)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `products-${tenantId}.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        // Defer revoke a tick — same-tick revoke can abort the download in some browsers.
        setTimeout(() => URL.revokeObjectURL(url), 0);
      })
      .catch((exportError: unknown) => {
        if (exportError instanceof Error) onError(exportError.message);
      });
  }, [employeeSession, tenantId, onError]);

  // After commit: products/categories patch via SSE; nudge only the group-count badge.
  const handleImported = useCallback(
    (result: ProductImportPreview) => {
      setIsImportOpen(false);
      void reloadVariantComboCounts();
      notifications.show({
        color: '#2563EB',
        message: `יובאו: ${result.summary.productsNew} חדשים, ${result.summary.productsUpdated} עודכנו`,
        autoClose: 3000,
      });
    },
    [reloadVariantComboCounts],
  );

  return {
    isImportOpen,
    openImport: useCallback(() => setIsImportOpen(true), []),
    closeImport: useCallback(() => setIsImportOpen(false), []),
    exportProducts,
    handleImported,
  };
}
